import React from 'react';
import { normalizeClassName } from '../../render/renderUtils';
import Markdown from 'marked-react';
import './RydUserSupportMainPanel.css';


const DOC_BODY_MD =`
# Eurydice User Guide

This document explains how to perform the main tasks in Eurydice and serves as a reference for all user-visible UI elements.

Note: Eurydice is a browser-based, audio-enabled app. Your browser will ask permission to use audio and MIDI devices. Grant access for input monitoring and recording.

- Audio: Web Audio API (your browser will prompt for permission).
- MIDI: Web MIDI API (required for external keyboard/controller input/output).



## A. Tutorials: Performing Common Tasks

### 1) Create a new project
1. Open Eurydice.
2. Create a project (e.g., “New Project”). A default tempo, time signature, and an empty note track are created.
3. If prompted, allow audio to start.

Tips:
- The initial project length and zoom factor are set automatically. You can later change zoom and track length from the track header and piano roll.

### 2) Import a MIDI file
1. Open the MIDI Import dialog.
2. Select a .mid/.midi file.
3. In the track list:
    - Review track names, instruments, and note counts.
    - Toggle “Include” per track to decide which ones to import.
4. Click Import.
5. Eurydice will:
    - Create a project sized to the longest imported track.
    - Add one Eurydice track per included MIDI track.
    - Set the project’s tempo and time signature from the file when available.

After import:
- You can assign instruments to the imported tracks and start playback.

### 3) Add a track
1. Use “Add Track” (or similar command in the track list/mixer).
2. A new track appears with default length and color.
3. Optionally assign an instrument and a MIDI input source (see sections below).

### 4) Edit notes in the Piano Roll
The piano roll shows time horizontally and pitch vertically.

- Add a note: Click on the grid where you want the note to start.
- Move a note: Drag the note block left/right (time) or up/down (pitch).
- Resize duration: Drag the right edge of a note block.
- Select notes:
    - Click to select one.
    - Drag to lasso-select multiple (when visual selection is enabled).
- Snap to grid: Toggle snap and adjust grid resolution to control quantization while editing.
- Velocity: When available, adjust per-note velocity through note controls (e.g., inspector or direct UI affordances).

### 5) Assign instruments (track output)
Each track can send notes to:
- Internal virtual synth (“VSYNTH”)
- Soundfont instrument (“SF2”)
- External MIDI device (“EXTMIDI”)

Steps:
1. Open Instrument/Output selection from the track header or roll I/O controls.
2. Choose an output type:
    - VSYNTH: Pick from built‑in virtual instruments.
    - SF2: Choose a GM soundfont instrument (e.g., “acoustic_grand_piano”).
    - EXTMIDI: Select a MIDI output device (synth/module/DAW).
3. Confirm. The track routes audio/MIDI to that destination.

### 6) Select a MIDI input (track input)
1. Open MIDI Input selection from the track header or roll I/O controls.
2. Choose:
    - Manual Note Input (enter notes via UI)
    - A connected MIDI device (keyboard/controller)
3. Optional: Enable Input Monitoring to hear notes as you play and to see activity.

### 7) Playback and metronome
- Play/Pause from the transport controls (and/or keybinding if available).
- The metronome ticks according to the project tempo and time signature.
- A caret runs over the piano roll in sync with playback.

Options:
- Loop playback of the current region/track(s).
- Mute/solo via the mixer to audition parts.
- Metronome mute toggle is available in playback settings.

### 8) Record MIDI
1. Arm the target track for recording.
2. Select a MIDI input device for that track and enable Input Monitoring if desired.
3. Press Record from the transport.
4. The metronome can provide a count‑in. Play your MIDI controller.
5. Stop or pause to finish. Captured notes are added to the track’s piano roll.

Tips:
- Ensure your browser has permission for MIDI devices.
- If you record multiple tracks, arm them individually; incoming MIDI is routed per assignment.

### 9) Mix the project
1. Open the Mixer.
2. For each track strip:
    - Adjust Volume (Gain) and Pan.
    - Toggle Mute/Solo.
    - Toggle Bus assignment (On Bus) to include/exclude it from the main mix.
3. Use the Master strip to control overall output level.

The mixer reflects changes live; depending on your system, rapid UI updates may affect performance at very low latencies.

### 10) Save and load projects
- Save creates a .ryd JSON file with tracks, instruments, sources, and mixer settings.
- Load opens a .ryd file and restores the workspace state:
    - Tracks, containers, instruments, sources, UI zoom, and mixer.

Advanced/CLI (optional):
- Convert MIDI to Eurydice save files or internal formats with the CLI (see Appendix).



## B. UI Reference

This section lists the main UI elements and how to use them.

### Workspace and Transport
- Play/Pause: Starts or pauses playback of all on‑bus tracks.
- Record: Arms transport for recording. With armed tracks, starts capture and places notes in the piano roll.
- Metronome: Toggles click on/off during playback and recording.
- Tempo (BPM): Sets playback speed. Affects metronome and scheduling.
- Time Signature: Sets the bar/beat structure (e.g., 4/4, 3/4). Affects metronome and grid.
- Loop: When enabled, loops playback for the active sequence/region.

### Track List and Headers
- Track Name: Click to rename.
- Track Color: Opens color picker for visual organization.
- Instrument/Output Selector:
    - VSYNTH: Choose a built‑in virtual instrument.
    - SF2: Choose a soundfont instrument by name.
    - EXTMIDI: Choose a MIDI output device (sends note on/off).
- Input Source Selector:
    - Manual Note Input or a hardware MIDI input.
- Record Arm: Enables recording into this track.
- Input Monitoring: Headphones icon; routes incoming MIDI to the assigned instrument for live audition.
- Grid Resolution: Opens a dialog to select musical resolution (bar, beat, division).
- Snap to Grid: Toggle to quantize edits to current grid resolution.
- Tonality Highlight: Toggle pitch highlighting based on the current scale/tonality.
- Collapsed/Expanded: Show/hide the track piano roll.

### Piano Roll
- Keybed (left): Displays note names; click to audition notes (instrument required).
- Note Blocks:
    - Click to add.
    - Drag to move (time/pitch).
    - Resize from right edge to change duration.
- Selection:
    - Click for single note.
    - Drag to box‑select (when visual selection mode is active).
- Caret: Indicates current playback position. Follows the transport.
- Scroll/Zoom: Mouse/trackpad gestures and header controls adjust the visible region.

### Roll I/O Controls
- MIDI Device Selection:
    - Inputs: Hardware controllers; Manual Note Input option.
    - Outputs: External MIDI sinks for EXTMIDI instruments.
- Playback Controls (per roll):
    - Local play/record shortcuts and indicators.
- Instrument Info:
    - Shows current instrument assignment; opens selection dialog.

### MIDI Import Dialog
- File Picker: Accepts .mid/.midi files.
- Track Table:
    - Channel index, track name, instrument, note count.
    - Include toggle per track.
- Import Button:
    - Creates a project with selected tracks.
    - Sets project tempo/time signature where available.

### Mixer Panel
- Track Strip:
    - Name: Track identifier.
    - Volume (Gain): Vertical slider (0–1).
    - Pan: Dial (-1 left to +1 right).
    - Mute (M): Silences the track.
    - Solo (S): Plays this track alone (or with other soloed tracks).
    - On Bus: Includes/excludes from master mix routing.
    - Meter: Displays level from the track’s audio node.
- Master Strip:
    - Master Volume: Controls the overall output gain.
    - Master Meter: Shows summed bus level.

Behavior:
- Solo logic: If any tracks are soloed, only soloed tracks on the bus are heard. Others are muted.
- On Bus: Tracks not on the bus are silent in the main mix (useful for staging or routing decisions).

### Metronome Pattern and Settings
- Pattern: Internal strong/weak beat pattern is generated from the time signature.
- Level: Clicks are synthesized; metronome can be muted.
- Count‑in: During record, a count‑in can be used to prepare before capture.

### Velocity and Dynamics
- Input velocity is captured from MIDI devices.
- Velocity may be adjustable per note in the roll or inspector (where available). Playback uses velocity where supported by instrument/output.

### Keybindings (where configured)
#### Workspace
- Play/Record:
    - Space: Play / Pause (playPause)
    - Ctrl + Space: Start / Stop Recording (record)
 - Metronome:
    - Ctrl + M: Toggle metronome on/off
 - Mode:
    - w: Toggle between NOTE ENTRY mode ("NOTES") and SELECTION mode ("SELECT")
 - Zoom:
    - -: Zoom out
    - +: Zoom in
 - Piano Roll:
    - Note Duration:
        - [: Decrease currently selected note input duration
        - ]: Increase currently selected note input duration
    - Grid Resolution:
        - {: Decrease grid resolution (coarser grid)
        - }: Increase grid resolution (finer grid)
 #### Note Editing (selection-active scope)
- Shift + ArrowUp: Transpose selected notes up one octave
- Shift + ArrowDown: Transpose selected notes down one octave
- Delete: Delete selected notes
- ArrowUp: Nudge selected notes up by one semitone
- ArrowDown: Nudge selected notes down by one semitone
- ArrowLeft: Nudge selected notes left in time (uses time signature context for quantized movement)
- ArrowRight: Nudge selected notes right in time (uses time signature context for quantized movement)
- D: Duplicate selected notes (time-aware; uses time signature + selection context)
- F: Fill gaps or extend selected notes (behavior defined in PianoRollEditKB.fillNotes)
- S: Split selected notes (NOTE: The description label in code says "Duplicate notes" but the handler is splitNotes -- likely a labeling bug.)

### Error/Status Messages
- Audio/MIDI permissions or device issues are surfaced via console/logs and UI toasts where applicable.
- If playback or mixer initialization fails, ensure the audio context is started and a compatible browser is used.
`;

export function RydUserSupportMainPanel() {
    return (
        <div className={normalizeClassName(["interaction-panel", "ryd-user-support-main-panel"])}
        >
            {/* <h2>User Support</h2> */}
            {/* <p>For support, please visit our <a href="https://support.example.com" target="_blank" rel="noopener noreferrer">support page</a>.</p> */}
            {/* <p>For feature requests or bug reports, please use our <a href="https://feedback.example.com" target="_blank" rel="noopener noreferrer">feedback form</a>.</p> */}
            {/* <p> */}
                For general inquiries, you can contact us at our discord server.
                {/* </p> */}

            <div style={{
                height: '400px',
                overflowY: 'scroll',
                padding: '2px',
                // paddingBottom: '400px',
                // boxShadow: 'inset 0 1px 6px rgba(0, 0, 0, 0.1)',
                // backgroundColor: 'color-mix(in srgb, var(--md-sys-color-secondary) 85%, white 15%)',
                backgroundColor: 'var(--md-sys-color-secondary-fixed)',

                }}>
                <Markdown>{DOC_BODY_MD}</Markdown>
            </div>
        </div>
    );
}